/**
 * views/RetakeScreen.tsx — /dashboard/retake
 * ─────────────────────────────────────────────
 * Mobile-optimised pilot retake queue.
 *
 * Shows every open scan exception for the pilot's active mission:
 *   • DETECTION_FAILURE  — Location QR / barcode unreadable
 *   • EMPTY_SLOT         — No stock found at location
 *   • requires_retake    — Engine confidence too low, re-fly needed
 *   • out_of_scope       — Photo outside allocated aisles
 *
 * Live updates arrive via the WebSocket channel that the Celery worker
 * publishes to: wss://{host}/ws/pilot/{sessionId}
 *
 * Pilot workflow
 * ─────────────
 * 1. Select active mission from the briefing strip.
 * 2. Connect live feed by entering the session UUID (same UUID used during
 *    file upload in PilotInterface).
 * 3. Walk the aisle — new DETECTION_FAILURE events pop into the queue.
 * 4. Re-fly flagged locations, then collapse completed items.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient, ApiError } from '@/api/client';
import { InfiniumMark } from '@/components/ui/InfiniumLogo';
import type { ExceptionRecord, Mission } from '@/types';

// ── Badge definitions ─────────────────────────────────────────────────────────

interface BadgeDef {
  label: string;
  color: string;       // CSS hex / var
  bg: string;
  icon: string;
}

const FLAG_BADGE: Record<string, BadgeDef> = {
  DETECTION_FAILURE: {
    label: 'Location QR Unreadable',
    color: '#fff',
    bg: '#dc2626',
    icon: '📵',
  },
  EMPTY_SLOT: {
    label: 'Empty Slot',
    color: '#1c1917',
    bg: '#f59e0b',
    icon: '📭',
  },
  MISMATCH_SKU: {
    label: 'SKU Mismatch',
    color: '#fff',
    bg: '#9333ea',
    icon: '⚠️',
  },
  DISCREPANCY_COUNT: {
    label: 'Count Discrepancy',
    color: '#1c1917',
    bg: '#fb923c',
    icon: '📊',
  },
};

const RETAKE_BADGE: BadgeDef = {
  label: 'Retake Required',
  color: '#fff',
  bg: '#b91c1c',
  icon: '🔄',
};

const SCOPE_BADGE: BadgeDef = {
  label: 'Out of Scope',
  color: '#fff',
  bg: '#6d28d9',
  icon: '🚧',
};

function getBadges(ex: ExceptionRecord): BadgeDef[] {
  const badges: BadgeDef[] = [];
  if (FLAG_BADGE[ex.flag]) badges.push(FLAG_BADGE[ex.flag]);
  if (ex.requires_retake)  badges.push(RETAKE_BADGE);
  if (ex.out_of_scope)     badges.push(SCOPE_BADGE);
  return badges;
}

// ── Time helpers ──────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

// ── Exception card ────────────────────────────────────────────────────────────

function ExceptionCard({
  ex,
  isNew,
  onDismiss,
}: {
  ex: ExceptionRecord;
  isNew: boolean;
  onDismiss: (id: string) => void;
}) {
  const badges = getBadges(ex);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss(ex.id);
  };

  return (
    <div className={`rtk-card${isNew ? ' rtk-card--new' : ''}`}>
      {/* Location code */}
      <div className="rtk-card-loc">
        <span className="rtk-card-loc-code">{ex.location_code}</span>
        <span className="rtk-card-time">{timeAgo(ex.created_at)}</span>
      </div>

      {/* Badges */}
      <div className="rtk-card-badges">
        {badges.map((b, i) => (
          <span
            key={i}
            className="rtk-badge"
            style={{ background: b.bg, color: b.color }}
          >
            {b.icon} {b.label}
          </span>
        ))}
      </div>

      {/* Reasoning */}
      {ex.reasoning && (
        <p className="rtk-card-reason">{ex.reasoning}</p>
      )}

      {/* SKU context */}
      {(ex.detected_sku || ex.expected_sku) && (
        <div className="rtk-card-sku">
          {ex.expected_sku && (
            <span className="rtk-sku-item rtk-sku-expected">
              Expected: <code>{ex.expected_sku}</code>
            </span>
          )}
          {ex.detected_sku && (
            <span className="rtk-sku-item rtk-sku-detected">
              Detected: <code>{ex.detected_sku}</code>
            </span>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="rtk-card-actions">
        <button
          className="rtk-btn rtk-btn--primary"
          onClick={handleDismiss}
          aria-label={`Mark ${ex.location_code} as re-flown`}
        >
          ✓ Re-flown
        </button>
        <button
          className="rtk-btn rtk-btn--ghost"
          onClick={handleDismiss}
          aria-label={`Skip ${ex.location_code}`}
        >
          Skip
        </button>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function RetakeScreen() {
  const { user, signOut } = useAuth();

  // Mission selection
  const [missions, setMissions]         = useState<Mission[]>([]);
  const [activeMissionId, setActiveMissionId] = useState<string>('');
  const [missionsLoading, setMissionsLoading] = useState(true);
  const [missionsError, setMissionsError] = useState<string | null>(null);

  // Exception list
  const [exceptions, setExceptions]   = useState<ExceptionRecord[]>([]);
  const [excLoading, setExcLoading]   = useState(false);
  const [excError, setExcError]       = useState<string | null>(null);
  const [newIds]                      = useState<Set<string>>(new Set());
  const [dismissed, setDismissed]     = useState<Set<string>>(new Set());

  // Live WebSocket feed
  const [sessionId, setSessionId]     = useState('');
  const [wsConnected, setWsConnected] = useState(false);
  const [liveCount, setLiveCount]     = useState(0);
  const wsRef = useRef<WebSocket | null>(null);

  // Polling (refresh every 30s when a mission is selected)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Load pilot's missions ──────────────────────────────────────────────────
  useEffect(() => {
    setMissionsLoading(true);
    apiClient
      .get<Mission[]>('/api/missions/mine')
      .then(data => {
        const active = data.filter(m =>
          m.status === 'ACTIVE' || m.status === 'PENDING',
        );
        setMissions(active);
        if (active.length === 1) setActiveMissionId(active[0].id);
      })
      .catch(e => setMissionsError(e instanceof ApiError ? e.message : 'Failed to load missions.'))
      .finally(() => setMissionsLoading(false));
  }, []);

  // ── Load / poll exceptions when mission changes ────────────────────────────
  const loadExceptions = useCallback(async (missionId: string, silent = false) => {
    if (!missionId) return;
    if (!silent) { setExcLoading(true); setExcError(null); }
    try {
      const data = await apiClient.get<ExceptionRecord[]>(
        `/api/missions/${missionId}/exceptions`,
      );
      setExceptions(data);
    } catch (e) {
      if (!silent) setExcError(e instanceof ApiError ? e.message : 'Failed to load exceptions.');
    } finally {
      if (!silent) setExcLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!activeMissionId) { setExceptions([]); return; }
    loadExceptions(activeMissionId);
    pollRef.current = setInterval(() => loadExceptions(activeMissionId, true), 30_000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeMissionId, loadExceptions]);

  // ── WebSocket live feed ────────────────────────────────────────────────────
  const connectWs = () => {
    if (!sessionId.trim()) return;
    wsRef.current?.close();
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${window.location.host}/ws/pilot/${sessionId.trim()}`);
    ws.onopen  = () => setWsConnected(true);
    ws.onclose = () => setWsConnected(false);
    ws.onerror = () => setWsConnected(false);
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data) as {
          event: string;
          photo_id?: string;
          data?: Record<string, unknown>;
        };
        if (msg.event === 'PING') return;
        if (
          msg.event === 'DETECTION_FAILURE' ||
          msg.event === 'EMPTY_SLOT'
        ) {
          setLiveCount(c => c + 1);
          // Refresh exception list immediately when a failure arrives
          if (activeMissionId) loadExceptions(activeMissionId, true);
        }
      } catch {
        // Malformed frame — ignore
      }
    };
    wsRef.current = ws;
  };

  const disconnectWs = () => {
    wsRef.current?.close();
    setWsConnected(false);
    setLiveCount(0);
  };

  // Cleanup WebSocket on unmount
  useEffect(() => () => { wsRef.current?.close(); }, []);

  // ── Dismiss handler ────────────────────────────────────────────────────────
  const handleDismiss = useCallback((id: string) => {
    setDismissed(prev => new Set(prev).add(id));
  }, []);

  // ── Derived state ──────────────────────────────────────────────────────────
  const activeMission  = missions.find(m => m.id === activeMissionId) ?? null;
  const visibleExcs    = exceptions.filter(e => !dismissed.has(e.id));
  const retakeCount    = visibleExcs.filter(e => e.requires_retake || e.flag === 'DETECTION_FAILURE').length;
  const outOfScopeCount = visibleExcs.filter(e => e.out_of_scope).length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="rtk-page">
      {/* ── Header ── */}
      <header className="rtk-header">
        <div className="rtk-header-left">
          <InfiniumMark variant="dark" height={28} />
          <h1 className="rtk-header-title">Retake Queue</h1>
          {wsConnected && (
            <span className="rtk-ws-live">
              ● Live{liveCount > 0 ? ` · ${liveCount} new` : ''}
            </span>
          )}
        </div>
        <div className="rtk-header-right">
          <span className="rtk-user-name">{user?.name || user?.email}</span>
          <button className="rtk-btn rtk-btn--ghost rtk-btn--sm" onClick={signOut}>
            Sign out
          </button>
        </div>
      </header>

      <main className="rtk-body">

        {/* ── Mission selector ── */}
        <section className="rtk-section">
          {missionsLoading && (
            <div className="rtk-placeholder">Loading missions…</div>
          )}
          {missionsError && (
            <div className="rtk-error">{missionsError}</div>
          )}
          {!missionsLoading && missions.length === 0 && (
            <div className="rtk-placeholder">
              <span className="rtk-placeholder-icon">🗺</span>
              <p>No active missions assigned to you.</p>
              <p className="rtk-placeholder-sub">Contact your manager to create or assign a mission.</p>
            </div>
          )}
          {missions.length > 1 && (
            <div className="rtk-mission-picker">
              <label className="rtk-label">Active Mission</label>
              <select
                className="rtk-select"
                value={activeMissionId}
                onChange={e => setActiveMissionId(e.target.value)}
              >
                <option value="">— Select a mission —</option>
                {missions.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.title ?? m.id.slice(0, 8)} · {m.allocated_aisles.length > 0
                      ? `Aisles: ${[...m.allocated_aisles].sort().join(', ')}`
                      : 'All aisles'}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Active mission banner */}
          {activeMission && (
            <div className="rtk-mission-banner">
              <div className="rtk-mission-banner-title">
                {activeMission.title ?? `Mission ${activeMission.id.slice(0, 8)}`}
              </div>
              <div className="rtk-mission-meta">
                {activeMission.allocated_aisles.length > 0 ? (
                  <span>
                    Aisles: <strong>{[...activeMission.allocated_aisles].sort().join(', ')}</strong>
                  </span>
                ) : (
                  <span>All aisles</span>
                )}
                <span className={`rtk-status-chip rtk-status-${activeMission.status.toLowerCase()}`}>
                  {activeMission.status}
                </span>
              </div>
            </div>
          )}
        </section>

        {/* ── Summary strip ── */}
        {activeMissionId && (
          <section className="rtk-summary-strip">
            <div className="rtk-summary-cell">
              <span className="rtk-summary-num rtk-summary-num--danger">{retakeCount}</span>
              <span className="rtk-summary-lbl">Retakes needed</span>
            </div>
            <div className="rtk-summary-cell">
              <span className="rtk-summary-num rtk-summary-num--warn">{outOfScopeCount}</span>
              <span className="rtk-summary-lbl">Out of scope</span>
            </div>
            <div className="rtk-summary-cell">
              <span className="rtk-summary-num">{visibleExcs.length}</span>
              <span className="rtk-summary-lbl">Total open</span>
            </div>
            <button
              className="rtk-btn rtk-btn--ghost rtk-btn--sm rtk-refresh-btn"
              onClick={() => loadExceptions(activeMissionId)}
              disabled={excLoading}
              aria-label="Refresh exception list"
            >
              {excLoading ? '…' : '↻'}
            </button>
          </section>
        )}

        {/* ── Exception list ── */}
        {activeMissionId && (
          <section className="rtk-section">
            {excLoading && exceptions.length === 0 && (
              <div className="rtk-placeholder">Loading exceptions…</div>
            )}
            {excError && (
              <div className="rtk-error">{excError}</div>
            )}
            {!excLoading && !excError && visibleExcs.length === 0 && (
              <div className="rtk-placeholder rtk-placeholder--ok">
                <span className="rtk-placeholder-icon">✅</span>
                <p>No open exceptions for this mission.</p>
                <p className="rtk-placeholder-sub">
                  All scanned locations are clean — equipment can be broken down.
                </p>
              </div>
            )}
            <div className="rtk-cards">
              {visibleExcs.map(ex => (
                <ExceptionCard
                  key={ex.id}
                  ex={ex}
                  isNew={newIds.has(ex.id)}
                  onDismiss={handleDismiss}
                />
              ))}
            </div>
          </section>
        )}

        {/* ── Live feed connector ── */}
        {activeMissionId && (
          <section className="rtk-section rtk-live-section">
            <h2 className="rtk-section-title">Live Scan Feed</h2>
            <p className="rtk-section-desc">
              Connect your active upload session to receive instant failure alerts.
            </p>
            <div className="rtk-live-connect">
              <input
                className="rtk-input"
                placeholder="Session UUID from the upload screen"
                value={sessionId}
                onChange={e => setSessionId(e.target.value)}
                disabled={wsConnected}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
              />
              {wsConnected ? (
                <button className="rtk-btn rtk-btn--danger" onClick={disconnectWs}>
                  Disconnect
                </button>
              ) : (
                <button
                  className="rtk-btn rtk-btn--primary"
                  onClick={connectWs}
                  disabled={!sessionId.trim()}
                >
                  Connect
                </button>
              )}
            </div>
            {wsConnected && (
              <p className="rtk-live-hint">
                ● Listening — new failures will appear instantly above.
              </p>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
